import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useHotkeys } from "react-hotkeys-hook";
import toast from "react-hot-toast";
import { PiArrowLeft, PiCaretLeft, PiCaretRight, PiCaretUpDown, PiTrash } from "react-icons/pi";

import API from "../../api";
import { capture } from "../../instrument";
import { SHORTCUTS } from "../../utils/shortcuts";
import { categoriesForType, categoryHotkey, criticalCategoriesForType, getTag, groupEntities, groupRecommendations, trimLineBreakLabels } from "../../components/annotation/labels";
import { uploadFilesToFolder } from "../../components/annotation/importAnnotationFiles";
import DocumentCanvas from "../../components/annotation/DocumentCanvas";
import ExamSearchInput from "../../components/annotation/ExamSearchInput";
import InspectorPanel from "../../components/annotation/InspectorPanel";
import LabelPicker from "../../components/annotation/LabelPicker";
import ShortcutHint from "../../components/shortcuts/ShortcutHint";

const docSnapshot = (doc) => JSON.stringify({ labels: doc?.labels, recommendedLabels: doc?.recommendedLabels, entities: doc?.entities?.filter((entity) => entity.polygon?.length || entity.active === false || entity.text) });

const AnnotationEditor = () => {
  const { folderId, annotationId } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();

  const [doc, setDoc] = useState(null);
  const [mode, setMode] = useState("stream");
  const [offset, setOffset] = useState(0);
  const [offsetInput, setOffsetInput] = useState("1");
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [picker, setPicker] = useState({ visible: false, x: 0, y: 0, indexes: null });
  const [focusRequest, setFocusRequest] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [drawForm, setDrawForm] = useState({ visible: false, x: 0, y: 0, polygon: null });
  const [drawText, setDrawText] = useState("");
  const [lastDeleted, setLastDeleted] = useState(null);
  const [highlightedEntity, setHighlightedEntity] = useState(null);
  const savedSnapshotRef = useRef(null);
  const labelChipRef = useRef(null);
  const requestedIdRef = useRef(null);

  const { data: folders = [] } = useQuery({
    queryKey: ["annotation-folders"],
    queryFn: async () => {
      const response = await API.get("/annotation-folder");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const folder = folders.find((item) => item._id === folderId);

  const categories = useMemo(() => categoriesForType(doc?.type || folder?.type), [doc?.type, folder?.type]);
  const entityGroups = useMemo(() => groupEntities(doc), [doc]);
  const recommendations = useMemo(() => groupRecommendations(doc), [doc]);
  const struckStarts = useMemo(
    () => new Set((doc?.entities || []).filter((entity) => entity.active === false && entity.reason !== "unchecked" && entity.start != null).map((entity) => entity.start)),
    [doc?.entities],
  );
  const uncheckedStarts = useMemo(
    () => new Set((doc?.entities || []).filter((entity) => entity.active === false && entity.reason === "unchecked" && entity.start != null).map((entity) => entity.start)),
    [doc?.entities],
  );
  const textByStart = useMemo(() => new Map((doc?.entities || []).filter((entity) => entity.text && entity.start != null && !entity.polygon?.length).map((entity) => [entity.start, entity.text])), [doc?.entities]);
  const scoreByStart = useMemo(() => new Map((doc?.entities || []).filter((entity) => entity.score != null && entity.start != null && !entity.polygon?.length).map((entity) => [entity.start, entity.score])), [doc?.entities]);
  const zoneEntities = useMemo(() => (doc?.entities || []).filter((entity) => entity.polygon?.length), [doc?.entities]);
  const isDirty = doc && docSnapshot(doc) !== savedSnapshotRef.current;

  const missingCategories = useMemo(() => {
    if (!doc) return [];
    const zoneLabels = new Set(zoneEntities.map((zone) => zone.label));
    return criticalCategoriesForType(doc.type).filter((category) => (!entityGroups[category] || entityGroups[category].items.length === 0) && !zoneLabels.has(category));
  }, [doc, entityGroups, zoneEntities]);

  const queryString = (extraParams = {}) => {
    const params = { folderId, ...extraParams };
    if (mode === "stream") params.reviewed = "false";
    if (mode === "browse" && debouncedSearch) params.q = debouncedSearch;
    return new URLSearchParams(params).toString();
  };

  const saveDoc = async (docToSave, { reviewed = false, silent = false } = {}) => {
    try {
      const { entities, ...payload } = docToSave;
      payload.labels = trimLineBreakLabels([...payload.labels], payload.tokens);
      payload.manualEntities = (entities || []).filter((entity) => entity.polygon?.length);
      payload.overrideEntities = (entities || []).filter((entity) => entity.start != null && !entity.polygon?.length && (entity.active === false || entity.text));
      const response = await API.put(`/annotation/${docToSave._id}`, { ...payload, reviewed });

      if (!response?.ok) throw new Error("Erreur lors de la sauvegarde du document");
      savedSnapshotRef.current = docSnapshot(docToSave);
      if (!silent) toast.success("Document sauvegardé");
      return true;
    } catch (error) {
      capture(error);
      toast.error("Erreur lors de la sauvegarde du document");
      return false;
    }
  };

  const syncUrl = (nextDoc) => {
    requestedIdRef.current = nextDoc?._id || null;
    const target = nextDoc ? `/annotation/${folderId}/${nextDoc._id}` : `/annotation/${folderId}`;
    if (history.location.pathname !== target) history.replace(target);
  };

  const fetchDoc = async (previous = doc) => {
    if (previous && docSnapshot(previous) !== savedSnapshotRef.current) {
      const saved = await saveDoc(JSON.parse(JSON.stringify(previous)), { silent: true });
      if (!saved) return;
    }

    try {
      const response = await API.get(`/annotation?${queryString({ offset, size: 1 })}`);
      const nextDoc = response.data.docs[0] || null;
      setDoc(nextDoc);
      savedSnapshotRef.current = docSnapshot(nextDoc);
      syncUrl(nextDoc);
    } catch (error) {
      capture(error);
      toast.error("Erreur lors de la récupération des données");
    }
  };

  const fetchDocById = async (id) => {
    if (requestedIdRef.current === id) return;
    requestedIdRef.current = id;

    try {
      const response = await API.get(`/annotation?q=${id}&size=1`);
      if (requestedIdRef.current !== id) return;
      const nextDoc = response.data.docs[0] || null;
      if (!nextDoc) {
        toast.error("Document introuvable — il a peut-être été supprimé");
        return fetchDoc(null);
      }
      setDoc(nextDoc);
      savedSnapshotRef.current = docSnapshot(nextDoc);
      syncUrl(nextDoc);
    } catch (error) {
      capture(error);
      toast.error("Erreur lors de la récupération des données");
    }
  };

  const fetchTotal = async () => {
    try {
      const response = await API.get(`/annotation?${queryString({ getTotal: "true", size: 1 })}`);
      setTotal(response.data.total);
    } catch (error) {
      capture(error);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchTotal();
    if (annotationId && annotationId !== doc?._id) {
      fetchDocById(annotationId);
      return;
    }
    fetchDoc();
  }, [offset, mode, folderId, debouncedSearch]);

  useEffect(() => {
    if (annotationId && annotationId !== doc?._id) fetchDocById(annotationId);
  }, [annotationId]);

  const onModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setOffset(0);
  };

  const onPrev = () => setOffset((previous) => Math.max(0, previous - 1));
  const onNext = () => setOffset((previous) => (previous + 1 < total ? previous + 1 : previous));

  useEffect(() => setOffsetInput(String(total === 0 ? 0 : offset + 1)), [offset, total]);

  const onJumpToDoc = () => {
    const number = parseInt(offsetInput, 10);
    if (!number || total === 0) return setOffsetInput(String(total === 0 ? 0 : offset + 1));
    setOffset(Math.min(Math.max(number, 1), total) - 1);
  };

  const afterReview = () => {
    toast.success("Document validé");
    queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
    fetchTotal();
  };

  const onValidate = async () => {
    if (!doc) return;

    if (missingCategories.length > 0) {
      const confirmed = confirm(`Entités critiques manquantes : ${missingCategories.join(", ")}.\n\nValider quand même ?`);
      if (!confirmed) return;
    }

    const saved = await saveDoc(doc, { reviewed: true, silent: true });
    if (!saved) return;

    afterReview();
    if (mode === "browse" && offset + 1 < total) setOffset(offset + 1);
    else fetchDoc(null);
  };

  const onDeleteDoc = async () => {
    if (!doc) return;
    const deletedDoc = doc;

    try {
      const response = await API.delete(`/annotation/${deletedDoc._id}`);
      if (!response?.ok) throw new Error("Erreur lors de la suppression du document");

      setLastDeleted(deletedDoc);
      toast.success("Document supprimé · Z pour annuler");
      queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
      fetchDoc(null);
      fetchTotal();
    } catch (error) {
      capture(error);
      toast.error("Erreur lors de la suppression du document");
    }
  };

  const onUndoDelete = async () => {
    if (!lastDeleted) return;
    const restored = { ...lastDeleted, deleted: false };

    try {
      const { entities, ...payload } = restored;
      payload.labels = trimLineBreakLabels([...payload.labels], payload.tokens);
      payload.manualEntities = (entities || []).filter((entity) => entity.polygon?.length);
      payload.overrideEntities = (entities || []).filter((entity) => entity.start != null && !entity.polygon?.length && (entity.active === false || entity.text));
      const response = await API.put(`/annotation/${restored._id}`, { ...payload, deleted: false });
      if (!response?.ok) throw new Error("Erreur lors de la restauration");

      setLastDeleted(null);
      setDoc(restored);
      savedSnapshotRef.current = docSnapshot(restored);
      syncUrl(restored);
      toast.success("Suppression annulée");
      queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
      fetchTotal();
    } catch (error) {
      capture(error);
      toast.error("Impossible d'annuler la suppression");
    }
  };

  const repairFollowingLabel = (labels, index) => {
    if (labels[index + 1]?.startsWith("I-")) labels[index + 1] = `B-${labels[index + 1].substring(2)}`;
  };

  const pruneStaleOverrides = (entities, labels) =>
    (entities || []).filter((entity) => {
      if (entity.polygon?.length || entity.start == null) return true;
      if (labels[entity.start] !== `B-${entity.label}`) return false;
      let end = entity.start + 1;
      while (end < labels.length && labels[end] === `I-${entity.label}`) end++;
      return entity.end == null || entity.end === end;
    });

  const onTagTokens = (indexes, { group = false, category = null } = {}) => {
    if (!doc) return;

    const categoryToApply = category || selectedCategory;
    const newLabels = [...doc.labels];

    for (let i = 0; i < indexes.length; i++) {
      const index = indexes[i];
      const previousIndex = i > 0 ? indexes[i - 1] : null;
      const bridgesLineBreaks = previousIndex !== null && index - previousIndex > 1 && doc.tokens.slice(previousIndex + 1, index).every((token) => token === "\n");

      const previousTag = newLabels.slice(0, index).findLast((tag) => getTag(tag)[1] !== "O");
      const [, previousEntity] = getTag(previousTag);

      let label = "O";
      if (categoryToApply) label = `B-${categoryToApply.name}`;
      if (categoryToApply && (previousIndex === index - 1 || bridgesLineBreaks)) label = `I-${categoryToApply.name}`;
      if (categoryToApply && group && previousEntity === categoryToApply.name) label = `I-${categoryToApply.name}`;
      if (indexes.some((j) => getTag(doc.labels[j])[1] === categoryToApply?.name)) label = "O";

      if (label.startsWith("I-") && bridgesLineBreaks) {
        for (let between = previousIndex + 1; between < index; between++) newLabels[between] = `I-${categoryToApply.name}`;
      }

      newLabels[index] = label;
      repairFollowingLabel(newLabels, index);
    }

    trimLineBreakLabels(newLabels, doc.tokens);
    setDoc((previous) => ({ ...previous, labels: newLabels, entities: pruneStaleOverrides(previous.entities, newLabels) }));
  };

  const onRemoveTokens = (indexes) => {
    if (!doc) return;

    const newLabels = [...doc.labels];
    for (const index of indexes) {
      newLabels[index] = "O";
      repairFollowingLabel(newLabels, index);
    }

    trimLineBreakLabels(newLabels, doc.tokens);
    setDoc((previous) => ({ ...previous, labels: newLabels, entities: pruneStaleOverrides(previous.entities, newLabels) }));
  };

  const resolveRecommendation = async (indexes, accept) => {
    if (!doc) return;
    if (indexes.length === 0) {
      if (recommendations.length === 0) return;
      indexes = recommendations[0].indexes;
    }

    const newLabels = [...doc.labels];
    const newRecommendedLabels = [...doc.recommendedLabels];

    for (const index of indexes) {
      if (accept) {
        newLabels[index] = newRecommendedLabels[index];
        if (!indexes.includes(index + 1)) repairFollowingLabel(newLabels, index);
      }
      newRecommendedLabels[index] = null;
    }

    trimLineBreakLabels(newLabels, doc.tokens);
    const newDoc = { ...doc, labels: newLabels, recommendedLabels: newRecommendedLabels, entities: pruneStaleOverrides(doc.entities, newLabels) };
    setDoc(newDoc);

    if (mode === "stream" && newRecommendedLabels.every((label) => label === null)) {
      const groups = groupEntities(newDoc);
      const zoneLabels = new Set((newDoc.entities || []).filter((entity) => entity.polygon?.length).map((entity) => entity.label));
      const stillMissing = criticalCategoriesForType(newDoc.type).filter((category) => (!groups[category] || groups[category].items.length === 0) && !zoneLabels.has(category));
      if (stillMissing.length > 0) return;

      const saved = await saveDoc(newDoc, { reviewed: true, silent: true });
      if (!saved) return;
      afterReview();
      fetchDoc(null);
    }
  };

  const onLocate = (indexes) => {
    if (!indexes || indexes.length === 0) return;
    setFocusRequest({ index: indexes[0], indexes, nonce: Date.now() });
  };

  useEffect(() => {
    setHighlightedEntity(null);
    setDrawForm({ visible: false, x: 0, y: 0, polygon: null });
  }, [doc?._id]);

  useEffect(() => {
    if (recommendations.length > 0) onLocate(recommendations[0].indexes);
  }, [doc?._id, recommendations[0]?.indexes?.[0]]);

  const openPickerAtChip = () => {
    const rect = labelChipRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPicker({ visible: true, x: rect.left, y: rect.bottom + 4, indexes: null });
  };

  const onPickerPick = (category) => {
    setSelectedCategory(category);
    if (picker.indexes?.length > 0) onTagTokens(picker.indexes, { category });
    setPicker({ visible: false, x: 0, y: 0, indexes: null });
  };

  const onDrawRegion = (polygon, position) => {
    setDrawText("");
    setDrawForm({ visible: true, x: position.x, y: position.y, polygon });
  };

  const onAddManualEntity = (text, category) => {
    setDoc((previous) => ({
      ...previous,
      entities: [...(previous.entities || []), { label: category.name, text, polygon: drawForm.polygon, source: "human", active: true }],
    }));
    setDrawForm({ visible: false, x: 0, y: 0, polygon: null });
  };

  const onRemoveManual = (manualIndex) => {
    setDoc((previous) => {
      const target = (previous.entities || []).filter((entity) => entity.polygon?.length)[manualIndex];
      return { ...previous, entities: (previous.entities || []).filter((entity) => entity !== target) };
    });
  };

  const updateZone = (zoneIndex, updater) => {
    setDoc((previous) => {
      const target = (previous.entities || []).filter((entity) => entity.polygon?.length)[zoneIndex];
      if (!target) return previous;
      return { ...previous, entities: (previous.entities || []).map((entity) => (entity === target ? updater(entity) : entity)) };
    });
  };

  const onToggleZoneInactive = (zoneIndex, reason) =>
    updateZone(zoneIndex, (zone) => {
      if (zone.active === false && (zone.reason || "struck") === reason) return { ...zone, active: true, reason: undefined };
      return { ...zone, active: false, reason };
    });

  const onToggleZoneStruck = (zoneIndex) => onToggleZoneInactive(zoneIndex, "struck");
  const onToggleZoneUnchecked = (zoneIndex) => onToggleZoneInactive(zoneIndex, "unchecked");

  const onEditZoneText = (zoneIndex, text) => {
    if (!text?.trim()) return;
    updateZone(zoneIndex, (zone) => ({ ...zone, text: text.trim() }));
  };

  const onToggleInactive = (indexes, reason) => {
    if (!doc) return;
    const start = indexes[0];
    const end = indexes[indexes.length - 1] + 1;
    const label = getTag(doc.labels[start])[1];
    setDoc((previous) => {
      const entities = [...(previous.entities || [])];
      const index = entities.findIndex((entity) => entity.start === start && !entity.polygon?.length);
      if (index === -1) {
        entities.push({ start, end, label, active: false, reason, source: "human" });
      } else if (entities[index].active === false && (entities[index].reason || "struck") === reason) {
        if (entities[index].text) entities[index] = { ...entities[index], active: true, reason: undefined };
        else entities.splice(index, 1);
      } else {
        entities[index] = { ...entities[index], active: false, reason };
      }
      return { ...previous, entities };
    });
  };

  const onToggleStruck = (indexes) => onToggleInactive(indexes, "struck");
  const onToggleUnchecked = (indexes) => onToggleInactive(indexes, "unchecked");

  const onEditEntityText = (indexes, text) => {
    if (!doc) return;
    const start = indexes[0];
    const end = indexes[indexes.length - 1] + 1;
    const label = getTag(doc.labels[start])[1];
    setDoc((previous) => {
      const entities = [...(previous.entities || [])];
      const index = entities.findIndex((entity) => entity.start === start && !entity.polygon?.length);
      if (index === -1) entities.push({ start, end, label, text: text || undefined, active: true, source: "human" });
      else entities[index] = { ...entities[index], text: text || undefined };
      return { ...previous, entities };
    });
  };

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length === 0) return;

      try {
        const uploaded = await uploadFilesToFolder(files, folderId);
        toast.success(`${uploaded} document(s) importé(s)`);
        queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
        fetchTotal();
      } catch (error) {
        capture(error);
        toast.error(error.message || "Erreur lors de l'import");
      }
    },
    [folderId],
  );

  useEffect(() => {
    const onDragOver = (event) => event.preventDefault();
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragover", onDragOver);

    return () => {
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", onDragOver);
    };
  }, [onDrop]);

  const hotkeyOpts = { preventDefault: true };
  useHotkeys(SHORTCUTS.ANNOTATION_PREV.keys, onPrev, hotkeyOpts, [total]);
  useHotkeys(SHORTCUTS.ANNOTATION_NEXT.keys, onNext, hotkeyOpts, [total]);
  useHotkeys(SHORTCUTS.ANNOTATION_VALIDATE.keys, onValidate, hotkeyOpts, [doc, mode, offset, total, missingCategories]);
  useHotkeys(SHORTCUTS.ANNOTATION_SAVE.keys, () => doc && isDirty && saveDoc(doc), { ...hotkeyOpts, enableOnFormTags: ["INPUT"] }, [doc, isDirty]);
  useHotkeys(SHORTCUTS.ANNOTATION_LABEL.keys, openPickerAtChip, hotkeyOpts, []);
  useHotkeys(SHORTCUTS.ANNOTATION_MANUAL.keys, () => setDrawMode((previous) => !previous), hotkeyOpts, []);
  useHotkeys(SHORTCUTS.ANNOTATION_DELETE.keys, onDeleteDoc, hotkeyOpts, [doc]);
  useHotkeys(SHORTCUTS.ANNOTATION_UNDO.keys, onUndoDelete, hotkeyOpts, [lastDeleted]);
  useHotkeys(SHORTCUTS.ANNOTATION_ACCEPT.keys, () => resolveRecommendation([], true), hotkeyOpts, [doc, recommendations, mode]);
  useHotkeys(SHORTCUTS.ANNOTATION_REJECT.keys, () => resolveRecommendation([], false), hotkeyOpts, [doc, recommendations, mode]);
  useHotkeys("escape", () => setSelectedCategory(null), hotkeyOpts, []);
  useHotkeys(
    categories.map((category) => categoryHotkey(category.key)).join(","),
    (event) => {
      const category = categories.find((item) => item.key === event.key.toLowerCase());
      if (category) setSelectedCategory(category);
    },
    hotkeyOpts,
    [categories],
  );

  const remaining = folder ? folder.total - folder.reviewed : 0;
  const progress = folder?.total > 0 ? Math.round((folder.reviewed / folder.total) * 100) : 0;

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex h-12 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          onClick={() => history.push("/annotation")}
        >
          <PiArrowLeft className="h-4 w-4" />
          Dossiers
        </button>

        <div className="h-5 w-px bg-gray-200" />

        <div className="flex min-w-0 items-center gap-3">
          <div className="truncate text-sm font-semibold text-gray-900">{folder?.name || "…"}</div>
          {folder && (
            <div className="flex shrink-0 items-center gap-2">
              <div className="h-1 w-20 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-xs tabular-nums text-gray-500">
                {folder.reviewed}/{folder.total}
              </div>
            </div>
          )}
        </div>

        <div className="flex rounded-lg border border-gray-200 p-0.5">
          <button
            type="button"
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${mode === "stream" ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
            onClick={() => onModeChange("stream")}
          >
            À réviser{mode === "stream" ? ` · ${total}` : remaining > 0 ? ` · ${remaining}` : ""}
          </button>
          <button
            type="button"
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${mode === "browse" ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
            onClick={() => onModeChange("browse")}
          >
            Tous{folder ? ` · ${folder.total}` : ""}
          </button>
        </div>

        {mode === "browse" && (
          <>
            <input
              className="w-56 rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              type="text"
              name="annotationSearch"
              aria-label="Rechercher un document"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setOffset(0);
              }}
              placeholder="Rechercher (@label:, @code:…)"
            />
            <div className="flex items-center gap-0.5 text-sm text-gray-500">
              <button type="button" aria-label="Document précédent" className="rounded-md p-1 hover:bg-gray-50 disabled:opacity-30" disabled={offset === 0} onClick={onPrev}>
                <PiCaretLeft className="h-4 w-4" />
              </button>
              <span className="flex items-center text-xs tabular-nums">
                <input
                  className="w-10 rounded border border-transparent bg-transparent px-0.5 text-right text-xs tabular-nums outline-none hover:border-gray-200 focus:border-primary-500"
                  aria-label="Numéro du document"
                  value={offsetInput}
                  onChange={(event) => setOffsetInput(event.target.value.replace(/\D/g, ""))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onJumpToDoc();
                    if (event.key === "Escape") setOffsetInput(String(total === 0 ? 0 : offset + 1));
                  }}
                  onBlur={onJumpToDoc}
                  onFocus={(event) => event.target.select()}
                />
                /{total}
              </span>
              <button type="button" aria-label="Document suivant" className="rounded-md p-1 hover:bg-gray-50 disabled:opacity-30" disabled={offset + 1 >= total} onClick={onNext}>
                <PiCaretRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {isDirty && <span className="text-xs text-gray-400">Non enregistré</span>}

          <button
            type="button"
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium ${drawMode ? "border-primary-500 bg-primary-50 text-primary-700" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
            onClick={() => setDrawMode((previous) => !previous)}
            title="Annoter manuellement : dessiner une zone, saisir le texte et le type (utile pour le manuscrit)"
          >
            Annotation manuelle <ShortcutHint id="ANNOTATION_MANUAL" />
          </button>

          <button
            ref={labelChipRef}
            type="button"
            className="flex items-center gap-2 rounded-lg border border-gray-200 py-1.5 pl-2.5 pr-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={openPickerAtChip}
            title="Choisir le label (L)"
          >
            <span className={`h-2 w-2 rounded-full ${selectedCategory?.bg || "bg-gray-300"}`} />
            <span className="max-w-32 truncate">{selectedCategory?.name || "Label"}</span>
            <PiCaretUpDown className="h-3.5 w-3.5 text-gray-400" />
          </button>

          <button
            type="button"
            aria-label="Supprimer le document"
            className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
            disabled={!doc}
            onClick={onDeleteDoc}
          >
            <PiTrash className="h-4 w-4" />
          </button>

          {isDirty && (
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 py-1.5 pl-3 pr-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
              onClick={() => saveDoc(doc)}
            >
              Enregistrer <ShortcutHint id="ANNOTATION_SAVE" />
            </button>
          )}

          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg bg-primary-500 py-1.5 pl-3 pr-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            disabled={!doc}
            onClick={onValidate}
            title="Valider et passer au suivant"
          >
            Valider <ShortcutHint id="ANNOTATION_VALIDATE" />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {doc ? (
          <>
            <div className="min-w-0 flex-1">
              <DocumentCanvas
                doc={doc}
                categories={categories}
                focusRequest={focusRequest}
                activeRecommendationIndexes={recommendations[0]?.indexes}
                onTagTokens={onTagTokens}
                onOpenPicker={(position, indexes) => setPicker({ visible: true, ...position, indexes })}
                drawMode={drawMode}
                manualEntities={zoneEntities}
                struckStarts={struckStarts}
                uncheckedStarts={uncheckedStarts}
                textByStart={textByStart}
                onDrawRegion={onDrawRegion}
                onRemoveManual={onRemoveManual}
                onHighlightEntity={(start) => setHighlightedEntity({ start })}
                onHighlightZone={(zoneIndex) => setHighlightedEntity({ zoneIndex })}
              />
            </div>
            <InspectorPanel
              doc={doc}
              categories={categories}
              entityGroups={entityGroups}
              recommendations={recommendations}
              missingCategories={missingCategories}
              struckStarts={struckStarts}
              uncheckedStarts={uncheckedStarts}
              textByStart={textByStart}
              scoreByStart={scoreByStart}
              zoneEntities={zoneEntities}
              highlightedEntity={highlightedEntity}
              onAcceptRecommendation={(indexes) => resolveRecommendation(indexes, true)}
              onRejectRecommendation={(indexes) => resolveRecommendation(indexes, false)}
              onRemoveToken={onRemoveTokens}
              onToggleStruck={onToggleStruck}
              onToggleUnchecked={onToggleUnchecked}
              onEditEntityText={onEditEntityText}
              onToggleZoneStruck={onToggleZoneStruck}
              onToggleZoneUnchecked={onToggleZoneUnchecked}
              onEditZoneText={onEditZoneText}
              onRemoveZone={onRemoveManual}
              onLocate={onLocate}
              onSelectCategory={setSelectedCategory}
            />
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-gray-400">
            {mode === "stream" ? (
              <>
                <div className="text-base font-medium text-gray-900">Tout est révisé</div>
                <div className="text-sm">Aucun document en attente dans ce dossier.</div>
                <button
                  type="button"
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                  onClick={() => onModeChange("browse")}
                >
                  Parcourir tous les documents
                </button>
              </>
            ) : (
              <div className="text-sm">Aucun document{debouncedSearch ? " pour cette recherche" : ""} — glissez des fichiers ici pour les importer.</div>
            )}
          </div>
        )}
      </div>

      <LabelPicker state={picker} categories={categories} onPick={onPickerPick} onClose={() => setPicker({ visible: false, x: 0, y: 0, indexes: null })} />

      {drawForm.visible && (
        <div className="fixed z-50 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg" style={{ left: drawForm.x, top: drawForm.y }}>
          <ExamSearchInput
            value={drawText}
            onChange={setDrawText}
            onPick={setDrawText}
            enabled={selectedCategory?.name === "ExamName"}
            autoFocus
            onEnter={() => selectedCategory && drawText.trim() && onAddManualEntity(drawText.trim(), selectedCategory)}
            onEscape={() => setDrawForm({ visible: false, x: 0, y: 0, polygon: null })}
            placeholder="Texte de l'entité"
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {categories.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`${item.bg} rounded-md px-2 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40 ${selectedCategory?.name === item.name ? "ring-2 ring-gray-900 ring-offset-1" : ""}`}
                disabled={!drawText.trim()}
                onClick={() => onAddManualEntity(drawText.trim(), item)}
              >
                {item.name}
              </button>
            ))}
          </div>
          {selectedCategory && <div className="mt-2 text-[11px] text-gray-400">Entrée = {selectedCategory.name}</div>}
        </div>
      )}
    </div>
  );
};

export default AnnotationEditor;
